import { Link } from 'react-router-dom';
import { Card } from '@/shared/ui/atoms';
import { Prose } from '@/features/onboarding/components/Prose';
import { LINK_GROUPS } from '@/features/onboarding/data/links';

/**
 * 첫 주 로드맵 — "언제까지 뭘 하면 정상인가"를 알려주는 게 목적이다.
 * 날짜가 아니라 순서를 담는다(입사일이 사람마다 다르고, 계정 발급 속도도 다르다).
 */
const ROADMAP = [
  {
    when: '첫날',
    goal: '🔑 계정과 환경',
    detail: '접근 권한을 한 번에 요청해 두고(발급에 하루씩 밀립니다) 그동안 개발 머신을 세팅해 주세요. 콘솔이 로컬에서 뜨면 1일차는 성공입니다! 🎉',
    to: '/onboarding/checklist',
    cta: '체크리스트 열기',
  },
  {
    when: '2~3일차',
    goal: '🛒 서비스 만져보기',
    detail: 'dev 웹과 어드민에서 주문을 한 번 끝까지 만들어 보세요. 도매/소매 모드 차이를 화면에서 먼저 보면 이해가 빠릅니다.',
    to: '/onboarding/links',
    cta: '링크 모음 열기',
  },
  {
    when: '첫 주',
    goal: '🧭 지형 파악',
    detail: '서비스맵으로 서비스 간 호출을 훑고, 담당 저장소의 문서·컨벤션을 읽어 주세요. 코드 탐색보다 문서가 빠릅니다.',
    to: '/onboarding/glossary',
    cta: '용어 · 저장소 지도',
  },
  {
    when: '2주차',
    goal: '🚀 첫 변경 배포',
    detail: '작은 이슈 하나를 이슈 → 브랜치 → PR → 머지 → 배포까지 통과시켜 봅니다. 크기보다 전 구간을 한 번 도는 게 중요합니다.',
    to: '/project-board/kanban',
    cta: '진행 중 과제 보기',
  },
];

const WORKFLOW = [
  { step: '📌 이슈', detail: '작업은 각 저장소의 GitHub Issues에서 출발합니다. `gh` CLI로 다룹니다' },
  { step: '🌿 브랜치 · 커밋', detail: '브랜치명과 커밋 메시지에 작업 식별자를 넣어 주세요 — 나중에 무엇이 왜 바뀌었는지 추적하는 유일한 끈입니다' },
  { step: '👀 PR 리뷰', detail: 'lint가 잡지 못하는 컨벤션이 많습니다. 리뷰 지적은 대부분 저장소 컨벤션 문서에 이미 적혀 있습니다' },
  { step: '🚀 배포', detail: 'main 머지 → GitHub Actions → (GKE는 ArgoCD 동기화) 순서로 나갑니다. 나간 뒤 Grafana에서 지표를 확인해 주세요' },
];

export function StartView(): React.JSX.Element {
  const essentials = LINK_GROUPS.flatMap((group) => group.links).filter((link) => link.essential);

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-lg border border-accent-line bg-accent-soft px-5 py-4">
        <h1 className="text-[15px] font-bold">👋 개발팀에 오신 걸 환영합니다!</h1>
        <p className="mt-1 max-w-3xl text-[13px] text-[#0a3069]">
          함께하게 되어 정말 반갑습니다. 이 가이드에는 첫 2주에 필요한 것만 모아 두었습니다 — 어디에 로그인하고, 무엇을
          설치하고, 어떤 순서로 읽고, 누구에게 요청하면 되는지. 천천히 따라오시면 됩니다 🙂 내용이 틀렸거나 빠졌다면{' '}
          <code className="rounded bg-white/70 px-1 font-mono text-[11.5px]">console-web/src/features/onboarding/data/</code>
          를 고쳐 PR을 올려 주세요. 첫 PR로도 아주 좋습니다 ✨
        </p>
      </section>

      <Card title="⭐ 매일 여는 것" hint="나머지는 링크 모음에서 찾을 수 있습니다">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
          {essentials.map((link) => (
            <a
              key={link.url + link.label}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-line px-3 py-2 hover:border-accent-line hover:bg-accent-soft"
            >
              <div className="text-[12.5px] font-semibold text-accent">{link.label} ↗</div>
              <div className="truncate text-[11px] text-faint">{link.url}</div>
            </a>
          ))}
        </div>
      </Card>

      <Card title="🗺️ 첫 2주 로드맵" hint="날짜가 아니라 순서입니다 — 계정 발급 속도에 따라 밀려도 괜찮습니다">
        <ol className="flex flex-col gap-2">
          {ROADMAP.map((phase) => (
            <li key={phase.when} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border border-line-soft px-3 py-2.5">
              <span className="w-14 shrink-0 text-[11px] font-bold text-muted">{phase.when}</span>
              <strong className="text-[13px]">{phase.goal}</strong>
              <Link to={phase.to} className="ml-auto text-[11.5px] text-accent hover:underline">
                {phase.cta} →
              </Link>
              <p className="w-full text-[12.5px] text-muted">{phase.detail}</p>
            </li>
          ))}
        </ol>
      </Card>

      <Card title="🔄 일이 흘러가는 방식" hint="이 순서를 한 번 돌면 온보딩은 끝입니다">
        <ol className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2">
          {WORKFLOW.map((phase, index) => (
            <li key={phase.step} className="rounded-md border border-line-soft p-3">
              <div className="text-[11px] font-bold text-faint">STEP {index + 1}</div>
              <strong className="text-[13px]">{phase.step}</strong>
              <p className="mt-0.5 text-[12px] text-muted"><Prose text={phase.detail} /></p>
            </li>
          ))}
        </ol>
      </Card>

      <Card title="🆘 막히면" hint="30분 넘게 같은 곳에서 막히면 편하게 물어봐 주세요 — 팀 규칙에 가깝습니다">
        <ul className="flex flex-col gap-1.5 text-[12.5px] text-muted">
          <li>
            <strong className="text-ink">🧰 환경·설치</strong> — 개발 환경 화면의 <Link to="/onboarding/setup" className="text-accent hover:underline">함정 목록</Link>을 먼저 봐 주세요. 대부분 여기 있습니다.
          </li>
          <li>
            <strong className="text-ink">🔒 권한이 안 열림</strong> — 체크리스트 항목의 요청처에 바로 요청해 주세요. 기다린다고 열리지 않습니다.
          </li>
          <li>
            <strong className="text-ink">🤔 도메인이 이해 안 됨</strong> — 저장소의 문서(CONTEXT·docs)를 먼저 읽고, 그래도 막히면 그 저장소를 최근에 만진 분께 물어봐 주세요.
          </li>
          <li>
            <strong className="text-ink">🚨 운영에서 뭔가 이상함</strong> — 혼자 파기 전에 슬랙 알림 채널부터 확인해 주세요. 이미 알려진 건일 때가 많습니다.
          </li>
        </ul>
      </Card>
    </div>
  );
}
